import React from 'react';
import { Alert, Box, Checkbox, FormControlLabel, FormHelperText, Switch, Typography } from '@mui/material';
import type { NahrazeniRequest, TypKontroly } from '../../../api/JsdhNotifikaceService/generated/models/TypKontroly';
import { maPushUpozorneni as typMaPush } from './upozorneniTypu';

export const TEXT_INFO_NAHRAZENI = 'Hasiči, kterých se tato kontrola týká, neuvidí vybrané kontroly. Nedostanou na ně ani upozornění. Příklad: Zdravotní prohlídka NDT (pro nositele dýchací techniky, 12 měsíců) nahrazuje Zdravotní prohlídku (24 měsíců). Nositel dýchací techniky má tak jen jednu prohlídku, ostatní hasiči dál tu obecnou. Nahrazení platí, jen když je tato kontrola aktivní a zobrazená.';
export const TEXT_BEZ_PODMINKY = 'Nahrazovat může jen kontrola, která platí pro určitou specializaci nebo funkci.';
export const TEXT_UZNAT_NAPOVEDA = 'Hasič, který do této kontroly nově spadne, si převezme datum poslední vybrané kontroly a nemusí ji zadávat znovu. Platnost se spočítá podle periody této kontroly.';
export const TEXT_VOLBA_SAMA_NAHRAZUJE = 'Tato kontrola už sama jinou nahrazuje';
export const TEXT_EDITOVANA_JE_NAHRAZENA = 'Tuto kontrolu už nahrazuje jiná';

interface Props {
    editovanyTypId: number | null;
    maPodminku: boolean;
    typy: TypKontroly[];
    value: NahrazeniRequest[];
    onChange: (value: NahrazeniRequest[]) => void;
    /** Ma editovana kontrola zapnuta push upozorneni (neprazdne intervaly). */
    maPushUpozorneni: boolean;
    /** Ma editovana kontrola zapnuty e-mail. */
    maEmailUpozorneni: boolean;
}

export const textVarovaniUpozorneni = (nazvy: string[]) =>
    `Nahrazovaná kontrola ${nazvy.join(', ')} posílá upozornění, tato kontrola ne. Hasiči, kterých se týká, by tak nedostávali žádná upozornění na blížící se konec platnosti. Zapněte upozornění i u této kontroly.`;

/** Pole „Nahrazuje kontroly“ ve formulari typu kontroly (spec 6.1 body 1 a 2). */
const NahrazujeKontrolyPole: React.FC<Props> = ({ editovanyTypId, maPodminku, typy, value, onChange, maPushUpozorneni, maEmailUpozorneni }) => {
    // Jednou uroven: kontrola, kterou uz nahrazuje jina, sama nic nahrazovat nesmi (spec 4.1 pravidlo 4)
    const editovanaJeNahrazena = editovanyTypId !== null && typy.some(
        (t) => t.id !== editovanyTypId && (t.nahrazuje ?? []).some((n) => n.typ.typId === editovanyTypId)
    );
    const volby = typy.filter((t) => (t.target ?? 'UZIVATEL') === 'UZIVATEL' && t.id !== editovanyTypId);

    const vybrana = (id: number) => value.find((v) => v.nahrazenyTypId === id);

    const duvodZakazu = (t: TypKontroly): string | null => {
        if (editovanaJeNahrazena) return TEXT_EDITOVANA_JE_NAHRAZENA;
        if ((t.nahrazuje ?? []).length > 0) return TEXT_VOLBA_SAMA_NAHRAZUJE;
        return null;
    };

    const prepni = (id: number, zapnout: boolean) => {
        if (zapnout) {
            onChange([...value, { nahrazenyTypId: id, uznatZaznam: false }]);
        } else {
            onChange(value.filter((v) => v.nahrazenyTypId !== id));
        }
    };

    const nastavUznat = (id: number, uznat: boolean) =>
        onChange(value.map((v) => (v.nahrazenyTypId === id ? { ...v, uznatZaznam: uznat } : v)));

    // Nahrazovana kontrola posila upozorneni kanalem, ktery editovana nema -> clenove by o konci platnosti nevedeli
    const nazvyBezUpozorneni = value
        .map((v) => typy.find((t) => t.id === v.nahrazenyTypId))
        .filter((t): t is TypKontroly => !!t)
        .filter((t) => (typMaPush(t) && !maPushUpozorneni) || (t.emailEnabled && !maEmailUpozorneni))
        .map((t) => t.nazev);

    return (
        <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Nahrazuje kontroly</Typography>
            <Alert severity="info" sx={{ mb: 1 }}>{TEXT_INFO_NAHRAZENI}</Alert>
            {!maPodminku && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{TEXT_BEZ_PODMINKY}</Typography>
            )}
            {volby.map((t) => {
                const polozka = vybrana(t.id);
                const duvod = maPodminku ? duvodZakazu(t) : null;
                // vybranou polozku jde vzdy odebrat, i kdyz by uz pridat nesla
                const vypnuto = !polozka && (!maPodminku || duvod !== null);
                return (
                    <Box key={t.id} sx={{ mb: 0.5 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={!!polozka}
                                    disabled={vypnuto}
                                    onChange={(e) => prepni(t.id, e.target.checked)}
                                />
                            }
                            label={t.nazev}
                        />
                        {vypnuto && duvod && (
                            <FormHelperText sx={{ ml: 4, mt: -0.5 }}>{duvod}</FormHelperText>
                        )}
                        {polozka && (
                            <Box sx={{ ml: 4 }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            size="small"
                                            checked={polozka.uznatZaznam}
                                            onChange={(e) => nastavUznat(t.id, e.target.checked)}
                                        />
                                    }
                                    label="Uznat poslední záznam"
                                />
                                <FormHelperText sx={{ mt: -0.5 }}>{TEXT_UZNAT_NAPOVEDA}</FormHelperText>
                            </Box>
                        )}
                    </Box>
                );
            })}
            {nazvyBezUpozorneni.length > 0 && (
                <Alert severity="warning" sx={{ mt: 1 }}>{textVarovaniUpozorneni(nazvyBezUpozorneni)}</Alert>
            )}
        </Box>
    );
};

export default NahrazujeKontrolyPole;
